import { apiConfig } from '@nomiss/configs';
import { ApiRequestError, executeSdkEnvelopeRequest } from './api-request.util';
import { adminApi, buildApiHeaders, fetchWithAuthRecovery } from './sdk-client';

export class AdminRolesError extends ApiRequestError {
  constructor(status: number, message: string) {
    super(status, message, 'AdminRolesError');
  }
}

export type AdminRole = {
  id: string;
  name: string;
  permissionCount: number;
};

export type AdminPermission = {
  id: string;
  name: string;
  description: string | null;
  allocatedToAdmin: boolean;
};

export type AdminUserRole = {
  userId: string;
  roles: string[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function parseRole(value: unknown): AdminRole | null {
  if (!isRecord(value) || typeof value.name !== 'string') return null;
  return {
    id: asString(value.id),
    name: asString(value.name),
    permissionCount: asNumber(value.permissionCount, 0),
  };
}

function parsePermission(value: unknown): AdminPermission | null {
  if (!isRecord(value) || typeof value.id !== 'string') return null;
  return {
    id: asString(value.id),
    name: asString(value.name),
    description:
      typeof value.description === 'string' ? value.description : null,
    allocatedToAdmin: value.allocatedToAdmin === true,
  };
}

export async function listAdminRoles(): Promise<AdminRole[]> {
  return executeSdkEnvelopeRequest({
    request: () => adminApi.adminControllerListRolesV1Raw(),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      if (!Array.isArray(envelope.data)) return [];
      return envelope.data
        .map(parseRole)
        .filter((item): item is AdminRole => item != null);
    },
    fallback: 'Failed to load roles',
    ErrorClass: AdminRolesError,
  });
}

export async function listAdminPermissions(): Promise<AdminPermission[]> {
  return executeSdkEnvelopeRequest({
    request: () => adminApi.adminControllerListPermissionsV1Raw(),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      if (!Array.isArray(envelope.data)) return [];
      return envelope.data
        .map(parsePermission)
        .filter((item): item is AdminPermission => item != null);
    },
    fallback: 'Failed to load permissions',
    ErrorClass: AdminRolesError,
  });
}

export async function getUserRoles(userId: string): Promise<AdminUserRole> {
  return executeSdkEnvelopeRequest({
    request: () => adminApi.adminControllerGetUserRolesV1Raw({ id: userId }),
    parse: (json) => {
      const envelope = isRecord(json) ? json : {};
      const data = isRecord(envelope.data) ? envelope.data : envelope;
      return {
        userId: asString(data.userId, userId),
        roles: Array.isArray(data.roles)
          ? data.roles.map((r) => asString(r))
          : [],
      };
    },
    fallback: 'Failed to load user roles',
    ErrorClass: AdminRolesError,
  });
}

export type UpdateUserRolesResult = {
  message: string;
};

export type UpdateAdminRolePermissionResult = {
  message: string;
};

export async function updateAdminRolePermission(
  permissionId: string,
  allocated: boolean,
): Promise<UpdateAdminRolePermissionResult> {
  const response = await fetchWithAuthRecovery(
    `${apiConfig.baseUrl}/v1/admin/roles/admin/permissions`,
    {
      method: 'PUT',
      headers: buildApiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ permissionId, allocated }),
    },
  );

  if (!response.ok) {
    const json = (await response.json().catch(() => ({}))) as unknown;
    const envelope = isRecord(json) ? json : {};
    const rawMessage = envelope.message;
    const message = Array.isArray(rawMessage)
      ? rawMessage.join(', ')
      : asString(rawMessage, 'Failed to update admin permission');
    throw new AdminRolesError(response.status, message);
  }

  const json = (await response.json()) as unknown;
  const envelope = isRecord(json) ? json : {};
  return {
    message: asString(envelope.message, 'Admin permission updated'),
  };
}

export async function updateUserRoles(
  userId: string,
  roles: string[],
): Promise<UpdateUserRolesResult> {
  return executeSdkEnvelopeRequest({
    request: () =>
      adminApi.adminControllerUpdateUserRolesV1Raw({
        id: userId,
        updateUserRolesDto: { roles: roles as any },
      }),
    parse: (json) => {
      if (isRecord(json) && typeof json.message === 'string') {
        return { message: json.message };
      }
      return { message: 'Roles updated' };
    },
    fallback: 'Failed to update user roles',
    ErrorClass: AdminRolesError,
  });
}
